import React, { useState, useEffect, useRef, useCallback } from "react";
import "./MathPractice.css";
import { awardToken } from "../../awardtoken.js";   /* <-- adjust path if needed */

/* ───────────────── Constants ───────────────── */
const SOUND_SOURCES = ["/sounds/correct.mp3", "/sounds/correct.ogg", "/sounds/correct.wav"];
const SHEET_SIZES   = [10, 25, 50];

const MODES = { SIDE: "side", ANGLE: "angle" };
const DIFFICULTY = {
  EASY:   { min: 3,  max: 12, decimals: false, label: "Easy"   },
  MEDIUM: { min: 5,  max: 25, decimals: false, label: "Medium" },
  HARD:   { min: 8,  max: 35, decimals: true,  label: "Hard"   },
};
const DEG2RAD = (d) => (d * Math.PI) / 180;
const RAD2DEG = (r) => (r * 180) / Math.PI;

const rand = (min, max, d = false) =>
  d ? parseFloat((Math.random() * (max - min) + min).toFixed(1))
    : Math.floor(Math.random() * (max - min + 1)) + min;

/* ──────────── Problem Generator ──────────── */
function genProblem({ mode = MODES.SIDE, diff = DIFFICULTY.MEDIUM } = {}) {
  const { min, max, decimals } = diff;

  // Always build a scaled right triangle with legs a,b and hyp c
  // We'll store raw lengths for answers; SVG is unit-less
  let a, b, c, θ;

  if (mode === MODES.SIDE) {
    // pick angle 15-75°, pick known side (adj or opp) then ask for another
    θ = rand(15, 75);                       // degrees
    const knownType = Math.random() < 0.5 ? "adj" : "opp";
    const knownLen  = rand(min, max, decimals);

    const rad = DEG2RAD(θ);
    const sin = Math.sin(rad);
    const cos = Math.cos(rad);

    if (knownType === "adj") {
      // we know side b (adjacent) => find opposite a
      b = knownLen;
      a = parseFloat((b * Math.tan(rad)).toFixed(2));
      c = parseFloat((b / cos).toFixed(2));
      return {
        shape: "triangle",
        dims: { a, b, c, θ },
        question: `In a right triangle, angle θ = ${θ}° and the adjacent side is ${b} units.\nFind the length of the opposite side (nearest 0.1).`,
        answer: parseFloat(a.toFixed(1)),
        prompt: "Find opposite side",
      };
    } else {
      // known opposite => find adjacent
      a = knownLen;
      b = parseFloat((a / Math.tan(rad)).toFixed(2));
      c = parseFloat((a / sin).toFixed(2));
      return {
        shape: "triangle",
        dims: { a, b, c, θ },
        question: `In a right triangle, angle θ = ${θ}° and the opposite side is ${a} units.\nFind the length of the adjacent side (nearest 0.1).`,
        answer: parseFloat(b.toFixed(1)),
        prompt: "Find adjacent side",
      };
    }
  }

  /* MODE = ANGLE */
  // choose two sides (opp/hyp, adj/hyp, opp/adj), ask for θ
  a = rand(min, max, decimals);
  b = rand(min, max, decimals);
  const pair = ["opp-hyp", "adj-hyp", "opp-adj"][rand(0, 2)];

  switch (pair) {
    case "opp-hyp": {
      c = rand(Math.max(a + 1, min), max + min, decimals); // ensure c > a
      θ = RAD2DEG(Math.asin(a / c));
      b = Math.sqrt(c * c - a * a);
      return {
        shape: "triangle",
        dims: { a, b, c, θ },
        question: `In a right triangle, the opposite side is ${a} units and hypotenuse is ${c} units.\nFind angle θ (nearest whole degree).`,
        answer: Math.round(θ),
        prompt: "Find θ",
      };
    }
    case "adj-hyp": {
      c = rand(Math.max(b + 1, min), max + min, decimals);
      θ = RAD2DEG(Math.acos(b / c));
      a = Math.sqrt(c * c - b * b);
      return {
        shape: "triangle",
        dims: { a, b, c, θ },
        question: `In a right triangle, the adjacent side is ${b} units and hypotenuse is ${c} units.\nFind angle θ (nearest whole degree).`,
        answer: Math.round(θ),
        prompt: "Find θ",
      };
    }
    default: { // opp-adj
      θ = RAD2DEG(Math.atan(a / b));
      c = Math.sqrt(a * a + b * b);
      return {
        shape: "triangle",
        dims: { a, b, c, θ },
        question: `In a right triangle, the opposite side is ${a} units and adjacent side is ${b} units.\nFind angle θ (nearest whole degree).`,
        answer: Math.round(θ),
        prompt: "Find θ",
      };
    }
  }
}

/* Helper for worksheets */
const genSet = (n, opts) => Array.from({ length: n }, () => genProblem(opts));

/* ───────────── SVG Illustration ───────────── */
const SvgText = ({ x, y, children, ...rest }) => (
  <text x={x} y={y} fontSize="12" textAnchor="middle" dominantBaseline="middle" {...rest}>
    {children}
  </text>
);

/* --- TriangleSVG: medium-sized, always fits --- */

/* --- TriangleSVG (large-fit) --- */
function TriangleSVG({ dims, showAngle = true }) {
  const TARGET = 115;                       // longest leg becomes 90 px
  const scale  = TARGET / Math.max(dims.a, dims.b);

  const a = dims.a * scale;                // vertical leg (opp)
  const b = dims.b * scale;                // horizontal leg (adj)

  const leftX = 10;                        // left margin
  const baseY = 110;                       // drop base lower for head-room
  const viewW = 160;                       // generous width
  const viewH = 140;                       // generous height

  return (
    <svg viewBox={`0 0 ${viewW} ${viewH}`} width="140" height="120">
      {/* right-triangle outline */}
      <polygon
        points={`${leftX},${baseY} ${leftX + b},${baseY} ${leftX},${baseY - a}`}
        fill="#eafcff"
        stroke="#2662a8"
        strokeWidth="2"
      />

      {/* right-angle square */}
      <rect
        x={leftX + b - 14}
        y={baseY - 14}
        width="14"
        height="14"
        fill="none"
        stroke="#2662a8"
        strokeWidth="2"
      />

      {/* angle label */}
      {showAngle && (
        <SvgText x={leftX + 22} y={baseY - 4} fontWeight="bold">
          {`θ=${Math.round(dims.θ)}°`}
        </SvgText>
      )}

      {/* side labels */}
      <SvgText x={leftX + b / 2} y={baseY + 14} fontWeight="bold">
        {`adj=${dims.b}`}
      </SvgText>

      <SvgText
        x={leftX - 6}
        y={baseY - a / 2}
        transform={`rotate(-90,${leftX - 6},${baseY - a / 2})`}
        fontWeight="bold"
      >
        {`opp=${dims.a}`}
      </SvgText>

      <SvgText x={leftX + b / 2} y={baseY - a / 2 - 12} fontSize="10">
        {`hyp=${dims.c.toFixed(1)}`}
      </SvgText>
    </svg>
  );
}



/* Worksheet column */
const WSCol = ({ problem }) => (
  <div className="math-col">
    <TriangleSVG dims={problem.dims} showAngle={problem.prompt === "Find θ"} />
    <p className="geometry-question" style={{ maxWidth:"200px", whiteSpace:"pre-wrap" }}>
      {problem.question}
    </p>
    <div className="math-underline" />
  </div>
);

/* ───────────── Main Component ───────────── */
export default function MathTrigPractice({ onAwardTokens } = {}) {
  const [mode,       setMode]       = useState(MODES.SIDE);
  const [difficulty, setDifficulty] = useState(DIFFICULTY.MEDIUM);
  const [problem,    setProblem]    = useState(() => genProblem({ mode, diff: difficulty }));
  const tokensAwarded = useRef(0);   // how many tokens this session
  const [ans,      setAns]      = useState("");
  const [attempts, setAttempts] = useState(0);
  const [correct,  setCorrect]  = useState(0);
  const [sheet,    setSheet]    = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);


  const audioRef = useRef(null);
    useEffect(() => {
  // Give a token each time the child reaches a new 10-multiple of correct
  if (correct >= (tokensAwarded.current + 1) * 10) {
    awardToken();
    tokensAwarded.current += 1;

    if (onAwardTokens) onAwardTokens(1);

    // Optional: refresh user from localStorage if you display coins/avatars, etc.
    // const updated = JSON.parse(localStorage.getItem("loggedInUser") || "null");
    // if (updated) setUser(updated);
  }
}, [correct, onAwardTokens]);

  /* regenerate when mode/difficulty changes */
  useEffect(() => {
    setProblem(genProblem({ mode, diff: difficulty }));
  }, [mode, difficulty]);

  /* sound */
  useEffect(() => {
    const audio = new Audio();
    for (const src of SOUND_SOURCES) {
      const type = src.endsWith("mp3") ? "audio/mpeg" : src.endsWith("ogg") ? "audio/ogg" : "audio/wav";
      if (audio.canPlayType(type)) { audio.src = src; break; }
    }
    audioRef.current = audio;
  }, []);

  /* helpers */
  const resetStats = useCallback(() => { setAttempts(0); setCorrect(0); }, []);
  const nextProblem = () => setProblem(genProblem({ mode, diff: difficulty }));

const submit = (e) => {
  e.preventDefault();
  if (!ans.trim() || waitingForNext) return;
  const user = parseFloat(ans);
  const ok = Math.abs(user - problem.answer) <= (mode === MODES.ANGLE ? 1 : 0.1);
  setAttempts(n => n + 1);
  if (ok) {
    setCorrect(n => n + 1);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    }
    setFeedback("✅ Correct!");
    setAns("");
    setTimeout(() => {
      setFeedback(null);
      nextProblem();
    }, 700);
    setWaitingForNext(false);
  } else {
    setFeedback(`❌ The correct answer is: ${problem.answer}`);
    setWaitingForNext(true);
  }
};


  /* Worksheet & PDF */
  const startSheet = (n) => {
    resetStats();
    setSheet(genSet(n, { mode, diff: difficulty }));
    setTimeout(() => window.print(), 50);
  };

  /* ───────── Render paths ───────── */
  if (sheet) {
    return (
      <div className="worksheet">
        <h2 className="no-print" style={{ display:"flex", justifyContent:"space-between" }}>
          Trigonometry Worksheet
          <span>
            <button onClick={() => setSheet(null)} style={{ marginLeft:"0.5rem" }}>Back to Practice</button>
          </span>
        </h2>
        <div className="sheet-grid">
          {sheet.map((p,i)=><WSCol key={i} problem={p} />)}
        </div>
      </div>
    );
  }

  /* ───────── Interactive practice ───────── */
  return (
    <div className="practice-container">
      <h2 style={{ display:"flex", justifyContent:"space-between" }}>
        Trigonometry Practice
        
      </h2>

      {/* Controls */}
      <div className="control-row">
        <label>
          Mode:&nbsp;
          <select value={mode} onChange={e=>setMode(e.target.value)}>
            <option value={MODES.SIDE}>Find Side</option>
            <option value={MODES.ANGLE}>Find Angle</option>
          </select>
        </label>
        <label style={{ marginLeft:"1rem" }}>
          Difficulty:&nbsp;
          <select value={difficulty.label} onChange={e=>setDifficulty(DIFFICULTY[e.target.value])}>
            {Object.entries(DIFFICULTY).map(([k,v])=>(
              <option key={k} value={k}>{v.label}</option>
            ))}
          </select>
        </label>
      </div>

      {/* Current Question */}
      <pre className="current-question" style={{ whiteSpace:"pre-wrap" }}>
        {problem.question}
      </pre>
      <TriangleSVG dims={problem.dims} showAngle={mode===MODES.ANGLE} />

      {/* Answer form */}
      <form onSubmit={submit} className="answer-form">
        <input
          type="number"
          step={mode===MODES.ANGLE ? "1" : "0.1"}
          value={ans}
          onChange={e=>setAns(e.target.value)}
          autoFocus
          placeholder={mode===MODES.ANGLE ? "θ (deg)" : "length"}
        />
        <button type="submit">Submit</button>
      </form>
{feedback && (
  <div className="math-practice-feedback" style={{ marginBottom: 7 }}>
    {feedback}
  </div>
)}
{waitingForNext && (
  <button
    className="math-pill-btn"
    style={{ marginTop: "0.7em" }}
    onClick={() => {
      setFeedback(null);
      setAns("");
      setWaitingForNext(false);
      nextProblem();
    }}
  >
    Next
  </button>
)}

      <p>Score: {correct} / {attempts}</p>

      {/* Worksheet buttons */}
      <div className="worksheet-buttons">
        <strong>Printable worksheets:</strong>
        {SHEET_SIZES.map(n=>(
          <button key={n} onClick={()=>startSheet(n)} style={{ marginLeft:"0.5rem" }}>
            {n} Questions
          </button>
        ))}
      </div>
    </div>
  );
}
